import { NotificationType, SupportTicketStatus } from '@repo/database/browser';
import type {
  CreateSupportTicketMessageResultDto,
  FetchSupportTicketDto,
  SupportTicketAttachmentDto,
  SupportTicketAttachmentUrlDto,
  SupportTicketDetailDto,
  SupportTicketListItemDto,
  SupportTicketMessageDto,
  UpdateSupportTicketStatusDto,
} from '@repo/api';

/**
 * Alias locali dei DTO condivisi del dominio ticket.
 *
 * Il contratto vive in `@repo/api` (una sola forma per backend, webapp e
 * backoffice): qui si dà solo il nome breve con cui i componenti della webapp
 * lo chiamano, senza ridichiarare campi che divergerebbero al primo cambio.
 */
export type SupportTicketListItem = SupportTicketListItemDto;
export type SupportTicketDetail = SupportTicketDetailDto;
export type SupportTicketMessage = SupportTicketMessageDto;
export type SupportTicketAttachment = SupportTicketAttachmentDto;
export type SupportTicketAttachmentUrl = SupportTicketAttachmentUrlDto;
export type CreateSupportTicketMessageResult =
  CreateSupportTicketMessageResultDto;
export type UpdateSupportTicketStatusPayload = UpdateSupportTicketStatusDto;

/**
 * Filtri della lista lato cliente. Del DTO condiviso si usa solo ciò che ha
 * senso dentro un tenant: `tenantId`, `assignedToId`, `onlyMine` e
 * `unassigned` sono strumenti della coda di backoffice e il controller
 * tenant-side li ignora, quindi non vanno nemmeno spediti.
 */
export type FetchSupportTicketsParams = Pick<
  FetchSupportTicketDto,
  | 'status'
  | 'active'
  | 'search'
  | 'sort'
  | 'skip'
  | 'take'
  | 'type'
  | 'category'
  | 'priority'
  | 'createdFrom'
  | 'createdTo'
>;

/**
 * Stati che il cliente può chiedere: chiudere una richiesta da sé, o
 * riaprirne una entro la finestra. Tutto il resto è mestiere dell'operatore,
 * e il backend lo rifiuterebbe comunque — averlo nel tipo lo rende un errore
 * di compilazione invece di un 400 in faccia all'utente.
 */
export type CustomerTicketStatus =
  | typeof SupportTicketStatus.CLOSED
  | typeof SupportTicketStatus.IN_PROGRESS;

/** Corpo e allegati di una risposta del cliente nel thread. */
export interface CreateSupportTicketMessagePayload {
  body: string;
  files?: File[];
}

/**
 * Notifiche che cambiano un ticket sotto gli occhi di chi lo sta leggendo.
 *
 * Una sola lista per i due consumatori — la rotta del click sulla notifica e
 * l'invalidazione della cache al suo arrivo — perche' aggiungere un tipo di
 * notifica di supporto a uno solo dei due significa o un deep-link che non
 * porta al ticket, o un dettaglio che resta fermo alla copia di prima.
 */
export const SUPPORT_TICKET_NOTIFICATION_TYPES: readonly NotificationType[] = [
  NotificationType.SUPPORT_TICKET_REPLIED,
  NotificationType.SUPPORT_TICKET_STATUS_CHANGED,
];

/**
 * `true` se la notifica riguarda un ticket. Predicato e non `includes` sul
 * chiamante perche' l'evento websocket porta il tipo come `string` grezza,
 * mentre la notifica persistita lo porta gia' tipizzato: il confronto sta in
 * un posto solo invece di due cast agli estremi.
 */
export function isSupportTicketNotificationType(type: string): boolean {
  return SUPPORT_TICKET_NOTIFICATION_TYPES.some((known) => known === type);
}
